import { useState } from "react";
import { useAuthStore, isConfigured, isQA } from "@music-app/sync-supabase";

interface Persona {
  id: string;
  label: string;
  email: string;
  description: string;
  badge: string;
}

interface PersonaGroup {
  label: string;
  personas: Persona[];
}

const PERSONA_GROUPS: PersonaGroup[] = [
  {
    label: "Ear Training",
    personas: [
      {
        id: "beginner",
        label: "Beginner",
        email: import.meta.env["VITE_DEMO_EMAIL_BEGINNER"] as string ?? "",
        description: "2 weeks in — major scale degrees + early intervals",
        badge: "bg-emerald-100 text-emerald-800 border-emerald-200",
      },
      {
        id: "intermediate",
        label: "Intermediate",
        email: import.meta.env["VITE_DEMO_EMAIL_INTERMEDIATE"] as string ?? "",
        description: "3 months in — multiple tracks, chords started",
        badge: "bg-blue-100 text-blue-800 border-blue-200",
      },
      {
        id: "advanced",
        label: "Advanced",
        email: import.meta.env["VITE_DEMO_EMAIL_ADVANCED"] as string ?? "",
        description: "9 months in — near-full curriculum coverage",
        badge: "bg-violet-100 text-violet-800 border-violet-200",
      },
    ],
  },
  {
    label: "Guitar Track",
    personas: [
      {
        id: "guitar_beginner",
        label: "Guitar — Beginner",
        email: import.meta.env["VITE_DEMO_EMAIL_GUITAR_BEGINNER"] as string ?? "",
        description: "3 weeks in — Rung 1 octave shapes",
        badge: "bg-amber-100 text-amber-800 border-amber-200",
      },
      {
        id: "guitar_intermediate",
        label: "Guitar — Intermediate",
        email: import.meta.env["VITE_DEMO_EMAIL_GUITAR_INTERMEDIATE"] as string ?? "",
        description: "3 months in — Rungs 1–2 done, triads starting",
        badge: "bg-orange-100 text-orange-800 border-orange-200",
      },
      {
        id: "guitar_advanced",
        label: "Guitar — Advanced",
        email: import.meta.env["VITE_DEMO_EMAIL_GUITAR_ADVANCED"] as string ?? "",
        description: "7 months in — CAGED shapes, Rung 5 started",
        badge: "bg-rose-100 text-rose-800 border-rose-200",
      },
    ],
  },
];

const ALL_PERSONAS = PERSONA_GROUPS.flatMap((g) => g.personas);

/**
 * Floating persona switcher for testing recommendation UX as different
 * user profiles. Visible only when:
 *   a) running in dev mode, OR
 *   b) the signed-in user has the `qa` role (production QA), OR
 *   c) already signed in as a demo persona — so a QA user isn't trapped after
 *      switching (persona accounts don't carry the qa role themselves).
 *
 * A plain `tester` (curriculum unlocked but not qa) never sees it in prod.
 *
 * Requires VITE_DEMO_EMAIL_* and VITE_DEMO_PASSWORD env vars, plus
 * the Supabase project to have the demo users seeded via `pnpm seed:personas`.
 */
export function DemoPersonaSwitch() {
  const user = useAuthStore((s) => s.user);
  const signInWithPassword = useAuthStore((s) => s.signInWithPassword);
  const signOut = useAuthStore((s) => s.signOut);

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const demoPassword = import.meta.env["VITE_DEMO_PASSWORD"] as string | undefined;
  const isDev = import.meta.env.DEV as boolean;

  const currentPersona = ALL_PERSONAS.find((p) => p.email && p.email === user?.email);

  // Hidden unless dev mode, a QA user, or already inside a persona.
  if (!isDev && !isQA(user) && !currentPersona) return null;
  // Hidden if Supabase isn't configured or demo password isn't set
  if (!isConfigured() || !demoPassword) return null;

  async function handleSwitch(persona: Persona) {
    if (!persona.email) {
      setError(`VITE_DEMO_EMAIL_${persona.id.toUpperCase()} is not set.`);
      return;
    }
    setBusy(true);
    setError(null);
    // Sign out current session first so auth state change fires cleanly
    if (user) await signOut();
    const { error: signInErr } = await signInWithPassword(persona.email, demoPassword!);
    if (signInErr) {
      setError(signInErr);
      setBusy(false);
      return;
    }
    // Reload so the sync engine re-initialises for the new user,
    // pulling their Supabase cards + prefs into fresh local state.
    window.location.reload();
  }

  async function handleSignOut() {
    setBusy(true);
    await signOut();
    setBusy(false);
    setOpen(false);
    window.location.reload();
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
      {open && (
        <div className="rounded-xl border border-ink/15 bg-paper shadow-xl p-3 w-64 text-sm">
          <div className="flex flex-col gap-2">
            {PERSONA_GROUPS.map((group, gi) => (
              <div key={group.label}>
                {gi > 0 && <div className="border-t border-ink/10 mt-1 mb-2" />}
                <p className="text-[10px] font-bold text-inkSoft uppercase tracking-widest mb-1 px-1">
                  {group.label}
                </p>
                <div className="flex flex-col gap-1">
                  {group.personas.map((persona) => {
                    const isCurrent = persona.email === user?.email;
                    return (
                      <button
                        key={persona.id}
                        disabled={busy || isCurrent}
                        onClick={() => void handleSwitch(persona)}
                        className={[
                          "w-full text-left px-3 py-2 rounded-lg border transition-all",
                          isCurrent
                            ? `${persona.badge} ring-2 ring-offset-1 ring-current opacity-80 cursor-default`
                            : `${persona.badge} hover:opacity-90 active:scale-[0.98]`,
                          busy && !isCurrent ? "opacity-40 pointer-events-none" : "",
                        ].join(" ")}
                        aria-pressed={isCurrent}
                      >
                        <span className="font-semibold block">{persona.label}</span>
                        <span className="text-[11px] opacity-70 block leading-tight">
                          {persona.description}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {currentPersona && (
            <button
              onClick={() => void handleSignOut()}
              disabled={busy}
              className="w-full mt-2 text-left px-3 py-1.5 rounded-lg text-xs text-inkSoft hover:bg-inkSoft/10 transition-colors"
            >
              Exit persona →
            </button>
          )}

          {error && (
            <p className="mt-2 px-1 text-[11px] text-red-600">{error}</p>
          )}
        </div>
      )}

      <button
        onClick={() => { setOpen((o) => !o); setError(null); }}
        className={[
          "rounded-full px-3 py-1.5 text-xs font-semibold shadow-md border transition-all",
          "flex items-center gap-1.5",
          currentPersona
            ? "bg-ink text-paper border-ink hover:bg-ink/80"
            : "bg-paper text-ink border-ink/20 hover:border-ink/60",
        ].join(" ")}
        title="Demo persona switcher"
        aria-expanded={open}
      >
        <span aria-hidden>👤</span>
        {currentPersona ? currentPersona.label : "Persona"}
      </button>
    </div>
  );
}
